import {
    ClaimType,
    ListProviderClaimsFilterClaimTypeEnum,
    ListProviderClaimsRequest,
    ListProviderGuarantorsRequest,
    ListProviderPatientsRequest,
    ListProviderPayersRequest,
    ListProviderProvidersRequest, ListSearchProviderClaimsRequest,
    ProviderClaim,
    ProviderGuarantor,
    ProviderPatient,
    ProviderPayer,
    ProviderProvider,
} from '@enter/core';
import _ from 'lodash';
import {action} from 'mobx';
import type {ClaimGenerator} from './claim.generator';
import {ClaimService} from './claim.service';
import {IFeatures} from './root.store';
import {
    accountNumberFilter,
    betweenDayBoundsFilter,
    equalsFilter,
    firstAndLastFilter,
    isDate,
    isDateTime,
    isNumber,
    isUuid,
    likeFilter,
    updateClaimsStatus,
} from './util';

export type ClaimSearchMode = 'patient' | 'payer' | 'guarantor' | 'provider';

export interface ISearchResult {
    claims?: ProviderClaim[];
    patients?: ProviderPatient[];
    payers?: ProviderPayer[];
    guarantors?: ProviderGuarantor[];
    providers?: ProviderProvider[];
}

export class SearchStore {
    private features: IFeatures;
    private claimService: ClaimService = new ClaimService();
    private claimGenerator: ClaimGenerator;

    constructor(features: IFeatures, claimGenerator: ClaimGenerator) {
        this.features = features;
        this.claimGenerator = claimGenerator;
    }

    @action public async searchForMode(mode: ClaimSearchMode, term: string, claimType: ClaimType): Promise<ISearchResult> {
        let searchResults: ISearchResult = {};
        if (mode === 'patient') {
            searchResults = await this.searchModePatient(term, claimType);
        } else if (mode === 'guarantor') {
            searchResults = await this.searchModeGuarantor(term, claimType);
        } else if (mode === 'provider') {
            searchResults = await this.searchModeProvider(term);
        } else if (mode === 'payer') {
            searchResults = await this.searchModePayer(term);
        }
        return searchResults;
    }

    private searchModePatient = async (term: string, claimType: ClaimType): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        let claims: ProviderClaim[] = [];

        claims = await this.fetchClaimSearch(searchTerm, claimType);

        return {
            claims,
        };
    };
    private searchModeGuarantor = async (term: string, claimType: ClaimType): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        let claims: ProviderClaim[] = [];
        let guarantors: ProviderGuarantor[] = [];

        const claimRequests = this.searchTerm2ClaimRequests(searchTerm, claimType);
        if (!_.isEmpty(claimRequests)) {
            // find claims, then find related guarantors
            claims = await this.fetchClaims(claimRequests);
            guarantors = await this.fetchGuarantors(this.claims2GuarantorRequests(claims));
        } else {
            // no claims matched, find matching guarantors and related claims
            guarantors = await this.fetchGuarantors(this.searchTerm2GuarantorRequests(searchTerm));
            claims = await this.fetchClaims(this.guarantors2ClaimRequests(guarantors, claimType));
        }

        return {
            claims,
            guarantors,
        };
    };

    private searchModePayer = async (term: string): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        const payers: ProviderPayer[] = await this.fetchPayers(this.searchTerm2PayerRequests(searchTerm));
        return {
            payers,
        };
    };

    private searchModeProvider = async (term: string): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        const providers: ProviderProvider[] = await this.fetchProviders(this.searchTerm2ProviderRequests(searchTerm));
        return {
            providers,
        };
    };

    private readonly searchTerm2ClaimRequests = (
        term: string,
        claimType: ClaimType,
        searchPatientNames: boolean = false,
    ): ListProviderClaimsRequest[] => {
        const filterClaimType: ListProviderClaimsFilterClaimTypeEnum = (claimType && claimType === ClaimType.INSTITUTIONAL) ? ListProviderClaimsFilterClaimTypeEnum.INSTITUTIONAL : ListProviderClaimsFilterClaimTypeEnum.PROFESSIONAL
        let requests: ListProviderClaimsRequest[] = [];
        if (isDateTime(term) || isDate(term)) {
            const filter: string[] | undefined = isDateTime(term) ? betweenDayBoundsFilter(term) : equalsFilter(term);
            requests.push({ filterPaymentDate: filter, filterClaimType});
            requests.push({ filterFromDos: filter, filterClaimType });
        } else if (isNumber(term) && _.size(term) > 5) {
            requests.push({ filterInvoiceNumber: equalsFilter(term), filterClaimType });
            requests.push({ filterAccountNumber: equalsFilter(term), filterClaimType });
        } else if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter, filterClaimType });
        } else if (searchPatientNames) {
            const filters = [...firstAndLastFilter(term).map(a=>{ return {...a, filterClaimType} }), ...accountNumberFilter(term).map(a=>{ return {...a, filterClaimType} })];
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly searchTerm2GuarantorRequests = (term: string): ListProviderGuarantorsRequest[] => {
        let requests: ListProviderGuarantorsRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            const filters = firstAndLastFilter(term);
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly searchTerm2PayerRequests = (term: string): ListProviderPayersRequest[] => {
        const requests: ListProviderPayersRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            requests.push({ filterName: likeFilter(term) });
        }
        return requests;
    };

    private readonly searchTerm2ProviderRequests = (term: string): ListProviderProvidersRequest[] => {
        let requests: ListProviderProvidersRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            const filters = firstAndLastFilter(term);
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly claims2GuarantorRequests = (claims: ProviderClaim[]): ListProviderGuarantorsRequest[] => {
        const requests: ListProviderGuarantorsRequest[] = [];
        if (!_.isEmpty(claims)) {
            const guarantorIds = _.uniq(_.map(claims, 'guarantorId'));
            const filters = _.compact(_.flatMap(guarantorIds, guarantorId => equalsFilter(guarantorId)));
            requests.push({
                filterId: filters,
            });
        }
        return requests;
    };

    private readonly claims2PatientRequests = (claims: ProviderClaim[]): ListProviderPatientsRequest[] => {
        const requests = [];
        // NB: only consider unique patient names
        const firsts = _.uniq(_.compact(_.map(claims, claim => claim.patientFirst)));
        if (!_.isEmpty(firsts)) {
            requests.push({ filterFirst: likeFilter(firsts) });
        }
        const lasts = _.uniq(_.compact(_.map(claims, claim => claim.patientLast)));
        if (!_.isEmpty(lasts)) {
            requests.push({ filterLast: likeFilter(lasts) });
        }
        return requests;
    };

    private readonly claims2PayerRequests = (claims: ProviderClaim[]): ListProviderPayersRequest[] => {
        const requests: ListProviderPayersRequest[] = [];
        if (!_.isEmpty(claims)) {
            const payerIds = _.uniq(_.map(claims, 'payerId'));
            const filters = _.compact(_.flatMap(payerIds, payerId => equalsFilter(payerId)));
            requests.push({
                filterId: filters,
            });
        }
        return requests;
    };

    private readonly claims2ProviderRequests = (claims: ProviderClaim[]): ListProviderProvidersRequest[] => {
        const requests: ListProviderProvidersRequest[] = [];
        if (!_.isEmpty(claims)) {
            const providerIds = _.uniq(_.map(claims, 'providerId'));
            const filters = _.compact(_.flatMap(providerIds, providerId => equalsFilter(providerId)));
            requests.push({
                filterId: filters,
            });
        }
        return requests;
    };

    private readonly guarantors2ClaimRequests = (guarantors: ProviderGuarantor[], claimType: ClaimType): ListProviderClaimsRequest[] => {
        const requests: ListProviderClaimsRequest[] = [];
        const filterClaimType: ListProviderClaimsFilterClaimTypeEnum = (claimType && claimType === ClaimType.INSTITUTIONAL) ? ListProviderClaimsFilterClaimTypeEnum.INSTITUTIONAL : ListProviderClaimsFilterClaimTypeEnum.PROFESSIONAL
        if (!_.isEmpty(guarantors)) {
            const guarantorIds = _.uniq(_.map(guarantors, 'id'));
            const filters = _.compact(_.flatMap(guarantorIds, guarantorId => equalsFilter(guarantorId)));
            requests.push({
                filterGuarantorId: filters,
                filterClaimType
            });
        }
        return requests;
    };

    private readonly payers2ClaimRequests = (payers: ProviderPayer[]): ListProviderClaimsRequest[] => {
        const requests: ListProviderClaimsRequest[] = [];
        if (!_.isEmpty(payers)) {
            const payerIds = _.uniq(_.map(payers, 'id'));
            const filters = _.compact(_.flatMap(payerIds, payerId => equalsFilter(payerId)));
            requests.push({
                filterPayerId: filters,
            });
        }
        return requests;
    };

    private readonly providers2ClaimRequests = (providers: ProviderProvider[]): ListProviderClaimsRequest[] => {
        const requests: ListProviderClaimsRequest[] = [];
        if (!_.isEmpty(providers)) {
            const providerIds = _.uniq(_.map(providers, 'id'));
            const filters = _.compact(_.flatMap(providerIds, providerId => equalsFilter(providerId)));
            requests.push({
                filterProviderId: filters,
            });
        }
        return requests;
    };

    private readonly fetchClaims = async (requests: ListProviderClaimsRequest[]): Promise<ProviderClaim[]> => {
        const claims = await (this.features.isDemo
            ? this.claimGenerator.loadClaims(requests)
            : this.claimService.fetchClaims(requests));

        return updateClaimsStatus(claims);
    };


    private readonly fetchClaimSearch = async (term: string, claimType: ClaimType): Promise<ProviderClaim[]> => {
        const claims = await (this.features.isDemo
            ? this.claimGenerator.loadClaims(this.searchTerm2ClaimRequests(term, claimType,true))
            : this.claimService.fetchClaimSearch({search: [term]}));
        return updateClaimsStatus(claims);
    };

    private readonly fetchGuarantors = async (
        requests: ListProviderGuarantorsRequest[],
    ): Promise<ProviderGuarantor[]> => {
        return this.features.isDemo
            ? this.claimGenerator.loadGuarantors(requests)
            : ClaimService.fetchGuarantors(requests);
    };
    private readonly fetchPatients = async (requests: ListProviderPatientsRequest[]): Promise<ProviderPatient[]> => {
        return this.features.isDemo ? this.claimGenerator.loadPatients(requests) : ClaimService.fetchPatients(requests);
    };
    private readonly fetchPayers = async (requests: ListProviderPayersRequest[]): Promise<ProviderPayer[]> => {
        return this.features.isDemo
            ? this.claimGenerator.loadPayers(requests)
            : this.claimService.fetchPayers(requests);
    };
    private readonly fetchProviders = async (requests: ListProviderProvidersRequest[]): Promise<ProviderProvider[]> => {
        return this.features.isDemo
            ? this.claimGenerator.loadProviders(requests)
            : ClaimService.fetchProviders(requests);
    };
}
